/**
 * Chat's pure helpers: names, short times, message groups, the task column's
 * order. No React, no API: the components and the tests use them.
 */
import { differenceInCalendarDays, format, isSameDay } from "date-fns"

/** A new group of bubbles starts after this long without a message from the same person. */
export const GROUP_GAP_MS = 5 * 60 * 1000

/** A time line goes between two messages this far apart. */
export const TIME_GAP_MS = 30 * 60 * 1000

const idOf = (value) => (value && typeof value === "object" ? value._id : value)

/**
 * The organisations chat is open in: every one.
 *
 * @param orgs {Object[]} app.state.orgs
 * @return {Object[]}
 */
export function chatOrgs(orgs) {
    return (orgs || []).filter((org) => org?._id)
}


/** The members of a conversation who are not me, as people ({_id, firstName, ...}). */
export function othersOf(view, meId) {
    return (view?.members || [])
        .map((m) => m.person)
        .filter((person) => person && idOf(person) !== meId)
        .map((person) => (typeof person === "string" ? { _id: person } : person))
}

export const fullName = (person) => [person?.firstName, person?.lastName].filter(Boolean).join(" ").trim()

/**
 * What a conversation is called: a group's name, else who is in it.
 *
 * @param view {Object}
 * @param meId {String}
 * @return {String}
 */
export function conversationTitle(view, meId) {
    if (view?.title) return view.title

    const others = othersOf(view, meId)

    if (view?.kind === "dm") return fullName(others[0]) || "Someone"
    if (!others.length) return "Just you"

    return others
        .map((p) => p.firstName)
        .filter(Boolean)
        .join(", ")
}

/**
 * The second line of a row in the list: who said what last.
 *
 * @param view {Object}
 * @param meId {String}
 * @return {String}
 */
export function previewLine(view, meId) {
    const last = view?.lastMessage

    if (!last?.text) return view?.kind === "group" ? "Group created" : "Say hi 👋"

    if (idOf(last.by) === meId) return `You: ${last.text}`
    if (view.kind !== "dm" && last.byName) return `${last.byName}: ${last.text}`

    return last.text
}

/**
 * How long ago, the way Messenger says it: now, 5m, 3h, 2d, 3w, then the date.
 *
 * @param date {*}
 * @param [now] {Date}
 * @return {String}
 */
export function shortAgo(date, now = new Date()) {
    if (!date) return ""

    const then = new Date(date)
    const seconds = Math.max(0, Math.floor((now - then) / 1000))

    if (seconds < 60) return "now"
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`

    const days = Math.floor(seconds / 86400)

    if (days < 7) return `${days}d`
    if (days < 28) return `${Math.floor(days / 7)}w`

    return format(then, then.getFullYear() === now.getFullYear() ? "d MMM" : "d MMM yyyy")
}

/**
 * The time line between messages: today's time, yesterday, a weekday, a date.
 *
 * @param date {*}
 * @param [now] {Date}
 * @return {String}
 */
export function timeLineLabel(date, now = new Date()) {
    const then = new Date(date)
    const days = differenceInCalendarDays(now, then)

    if (isSameDay(now, then)) return format(then, "HH:mm")
    if (days === 1) return `Yesterday ${format(then, "HH:mm")}`
    if (days < 7) return format(then, "EEE HH:mm")

    return format(then, then.getFullYear() === now.getFullYear() ? "d MMM HH:mm" : "d MMM yyyy HH:mm")
}

/** Where a pasted image may come from: our own file links (file.utils getFileUrl). */
const IMAGE_HOSTS = [process.env.REACT_APP_API_SERVICE_HOST, "https://api.missionx.ai", "https://dev.api.missionx.ai"]
    .filter(Boolean)
    .map((host) => host.replace(/\/+$/, ""))

/** A pasted image, exactly as the composer writes it. */
const IMAGE_MESSAGE = /^<img src="([^"<>\s]+)"\s*\/?>$/

/**
 * A pasted image's link, when the message is exactly one image of ours
 * (`<img src="<API>/drive/d/<id>/<name>"/>`). Anything else is text, shown as typed.
 * Without its query: an image pasted on localhost once carried a sign-in token.
 *
 * @param text {String}
 * @return {String|null}
 */
export function imageUrlOf(text) {
    const url = String(text || "")
        .trim()
        .match(IMAGE_MESSAGE)?.[1]
        ?.split(/[?#]/)[0]

    if (!url) return null

    return IMAGE_HOSTS.some((host) => url.startsWith(`${host}/drive/d/`)) ? url : null
}

/** A pasted image of ours (imageUrlOf). */
export const isImageMessage = (text) => Boolean(imageUrlOf(text))

/**
 * Messages as the thread draws them: time lines, and runs of bubbles by one person.
 *
 * @param messages {Object[]} Oldest first
 * @param meId {String}
 * @return {Array<{type: "time", at: *, key: String}|{type: "group", mine: Boolean, author: Object, messages: Object[], key: String}>}
 */
export function groupMessages(messages, meId) {
    const items = []
    let group = null
    let previous = null

    ;(messages || []).forEach((message) => {
        const at = new Date(message.createdAt || Date.now()).getTime()
        const authorId = idOf(message.createdBy)
        const gap = previous ? at - previous : Infinity

        if (gap >= TIME_GAP_MS) {
            items.push({ type: "time", at: message.createdAt, key: `t-${message._id}` })
            group = null
        }

        if (!group || group.authorId !== authorId || gap >= GROUP_GAP_MS) {
            group = {
                type: "group",
                key: `g-${message._id}`,
                authorId,
                author: typeof message.createdBy === "object" ? message.createdBy : { _id: authorId },
                mine: authorId === meId,
                messages: [],
            }
            items.push(group)
        }

        group.messages.push(message)
        previous = at
    })

    return items
}

/**
 * Of my messages, the last one the other person in a direct message has read.
 *
 * @param view {Object} The conversation
 * @param messages {Object[]} Oldest first
 * @param meId {String}
 * @return {String|null} Its id
 */
export function lastSeenMessageId(view, messages, meId) {
    if (view?.kind !== "dm") return null

    const other = (view.members || []).find((m) => idOf(m.person) !== meId)
    const readAt = other?.lastReadAt ? new Date(other.lastReadAt).getTime() : 0

    if (!readAt) return null

    for (let i = (messages || []).length - 1; i >= 0; i--) {
        const message = messages[i]

        if (idOf(message.createdBy) === meId && new Date(message.createdAt).getTime() <= readAt) {
            return message._id
        }
    }

    return null
}

const recency = (task) =>
    Math.max(new Date(task.lastSelectedAt || 0).getTime(), new Date(task.lastMessageAt || 0).getTime())

/**
 * The task column: projects, each with its tasks. Most recently picked (or
 * talked in) first, then by title; a search narrows the tasks and the projects.
 *
 * @param missions {Object[]}
 * @param tasks {Object[]}
 * @param [query] {String}
 * @return {Array<{mission: Object, tasks: Object[], unread: Number}>}
 */
export function tasksByMission(missions, tasks, query) {
    const q = String(query || "")
        .trim()
        .toLowerCase()
    const matches = (task, mission) =>
        !q ||
        String(task.title || "")
            .toLowerCase()
            .includes(q) ||
        String(task.code || "")
            .toLowerCase()
            .includes(q) ||
        String(mission?.title || "")
            .toLowerCase()
            .includes(q)

    const byMission = new Map((missions || []).map((m) => [m._id, { mission: m, tasks: [], unread: 0, recent: 0 }]))

    ;(tasks || []).forEach((task) => {
        const entry = byMission.get(task.missionId)

        if (!entry || !matches(task, entry.mission)) return

        entry.tasks.push(task)
        entry.unread += task.unread || 0
        entry.recent = Math.max(entry.recent, recency(task))
    })

    return [...byMission.values()]
        .filter((entry) => entry.tasks.length)
        .map((entry) => ({
            ...entry,
            tasks: entry.tasks.sort(
                (a, b) => recency(b) - recency(a) || String(a.title).localeCompare(String(b.title)),
            ),
        }))
        .sort(
            (a, b) => b.recent - a.recent || String(a.mission.title).localeCompare(String(b.mission.title)),
        )
        .map(({ recent, ...entry }) => entry)
}

/**
 * The conversations shown in the first column, narrowed by a search over names and the group's name.
 *
 * @param list {Object[]}
 * @param query {String}
 * @param meId {String}
 * @return {Object[]}
 */
export function filterConversations(list, query, meId) {
    const q = String(query || "")
        .trim()
        .toLowerCase()

    if (!q) return list || []

    return (list || []).filter(
        (view) =>
            conversationTitle(view, meId).toLowerCase().includes(q) ||
            othersOf(view, meId).some((p) => fullName(p).toLowerCase().includes(q)),
    )
}


/** The composer's emojis: the task thread's own set (comp/Messages.jsx), so both chats offer the same. */
export const CHAT_EMOJIS = [
    "👍",
    "👎",
    "❤️",
    "🥋",
    "🙂",
    "🎉",
    "🔥",
    "😔",
    "☠️",
    "🦖",
    "🤔",
    "💡",
    "🌶️",
    "🤣",
    "😠",
    "🤷",
    "💥",
    "👆",
    "👇",
    "🙌",
    "🐳",
    "🙏",
    "💪",
    "🤞",
    "🖐️",
    "👏",
]

/**
 * Puts an emoji into the composer's text: at the caret, or at the end when the
 * text holds a mention (the caret counts the shown text, not its markup).
 *
 * @param value {String} The composer's value
 * @param emoji {String}
 * @param [caret] {Number} selectionStart of the textarea
 * @return {{value: String, caret: Number}}
 */
export function insertEmoji(value, emoji, caret) {
    const text = String(value || "")
    const at = text.includes("@[") || !Number.isFinite(caret) ? text.length : Math.min(Math.max(caret, 0), text.length)

    return { value: text.slice(0, at) + emoji + text.slice(at), caret: at + emoji.length }
}

/**
 * A mission's theme as a small round picture: its image, over its colour.
 *
 * @param mission {Object} { theme: { backgroundImage: "url(...)", backgroundColor: "999999" } }
 * @return {Object} Inline style
 */
export function missionAvatarStyle(mission) {
    const color = mission?.theme?.backgroundColor

    return {
        backgroundImage: mission?.theme?.backgroundImage || undefined,
        backgroundColor: color ? `#${String(color).replace("#", "")}` : undefined,
    }
}
